import { z } from "zod";
/**
 * Who is making a request.
 *
 * Three types, not two. `guest` exists because the platform serves anonymous
 * visitors who may ask general questions but must never reach account data —
 * modelling that as "customer with fewer permissions" would mean an unauthenticated
 * caller carries a customer principal, and one missing check would expose orders.
 * A distinct type makes that mistake a type error rather than a policy oversight.
 */
export const principalTypeSchema = z.enum(["guest", "customer", "agent"]);
/**
 * Internal staff roles, ordered by privilege.
 *
 * `supervisor` sits between agent and admin so that day-to-day escalation
 * handling does not require the account-management privileges that `admin` holds.
 */
export const agentRoleSchema = z.enum(["agent", "supervisor", "admin"]);
/**
 * Which of a browser's two possible sessions a credential belongs to.
 *
 * The platform's cookies are scoped to the registrable parent domain, because the
 * shop, the dashboard, the API and the realtime service are four different
 * subdomains that must all see the same session. That scope has a consequence: one
 * browser holds *one* cookie jar for all of them.
 *
 * Support staff are also customers. An administrator signed into the dashboard who
 * opens the shop in another tab is one person with two legitimate, simultaneous
 * sessions — and with a single set of cookie names the second login silently
 * overwrites the first, so opening the shop signs them out of the dashboard and the
 * chat widget answers as the administrator.
 *
 * Splitting the cookie names by audience is what lets both exist at once. It is not
 * a security boundary on its own — every route still checks permissions — but it
 * removes the ambiguity that made a single browser unable to hold both roles.
 */
export const sessionAudienceSchema = z.enum(["staff", "customer"]);
/**
 * The audience a principal's session belongs to.
 *
 * Derived from the principal rather than declared by the caller, so a token issued
 * for one audience can be detected in the other's cookie and refused.
 */
export function audienceOf(principal) {
    return principal.type === "agent" ? "staff" : "customer";
}
/**
 * How a browser client states which of its two sessions it is acting as.
 *
 * The audience cannot be inferred from the request: the dashboard calls customer
 * routes when an agent replies to a conversation, so the URL says nothing, and when
 * both cookies are present "whichever is in the jar" is ambiguous by construction.
 * The client is the only party that knows which role it is playing.
 *
 * Neither of these authorises anything. They select which credential to read, and
 * that credential is then verified exactly as before — naming an audience whose
 * cookie you do not hold yields an anonymous request, not somebody else's session.
 *
 * Declared here rather than in the Fastify layer because the realtime service and
 * the API both need them and only one of the two is built on Fastify plugins.
 */
export const AUDIENCE_HEADER = "x-island-audience";
/**
 * The same selector, for requests that cannot carry headers.
 *
 * A WebSocket handshake and an `<img src>` are both plain browser-issued GETs with
 * nowhere to put a header. Unlike a token, an audience name is not a credential: it
 * is safe in a URL, an access log, and browser history — which is why the realtime
 * handshake refuses tokens in the query string but accepts this.
 */
export const AUDIENCE_QUERY_PARAM = "audience";
export function isGuest(principal) {
    return principal.type === "guest";
}
export function isCustomer(principal) {
    return principal.type === "customer";
}
export function isAgent(principal) {
    return principal.type === "agent";
}
//# sourceMappingURL=principal.js.map